import { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useStore } from '@/store/useStore';
import { setGameContext } from '@/store/actions';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';
import {
  GAME_LAUNCHER_UM_EXPERIMENT,
  getExperimentValue,
} from '@/utilities/logger/amplitude/experiments/getExperimentValue';
import { LAUNCH_METHOD_STORAGE_KEY } from '@/utilities/helpers/launchNextGame';

const DEFAULT_LAUNCH_METHOD = 'traditional-iframe';

/**
 * Once the game context is resolved, hydrates `launchMethod` from sessionStorage
 * (written by the unified-module launcher before navigation) into the store and
 * emits a `GAME_LAUNCH_INIT` RUM action including the launch method and the
 * `game_launcher_um_enable` experiment value. Fires once per page load.
 */
export const useLogGameLaunched = () => {
  const { gameContext, runtime, amplitudeExperiments } = useStore(
    useShallow((s) => ({
      gameContext: s.gameContext,
      runtime: s.runtime,
      amplitudeExperiments: s.amplitudeExperiments,
    })),
  );

  const hasLogged = useRef(false);

  const { rgi, brand, gameName, gameId, provider, providerRef, gameUid } =
    gameContext;

  useEffect(() => {
    if (hasLogged.current) return;

    if (!rgi || !gameName || !gameId || !provider || !providerRef) {
      return;
    }

    hasLogged.current = true;

    const storedLaunchMethod = sessionStorage.getItem(
      LAUNCH_METHOD_STORAGE_KEY,
    );
    if (storedLaunchMethod) {
      setGameContext({ launchMethod: storedLaunchMethod });
      sessionStorage.removeItem(LAUNCH_METHOD_STORAGE_KEY);
    }

    const launchMethod = storedLaunchMethod || DEFAULT_LAUNCH_METHOD;
    const gameLauncherUMExperiment =
      getExperimentValue(amplitudeExperiments, GAME_LAUNCHER_UM_EXPERIMENT) ??
      getExperimentValue(amplitudeExperiments, 'gameLauncherUMEnabled') ??
      'unknown';

    Logger.logRumAction({
      name: '[GW][GAME_LAUNCH] Initiated',
      useCase: DdEventCategory.GameLaunch,
      eventType: DdEventType.Action,
      payload: {
        launchMethod,
        gameLauncherUMExperiment,
        rgi,
        brand: brand ?? runtime.brand,
        platform: runtime.platform,
        gameProvider: provider,
        gameName,
        gameId,
        providerRef,
        gameUid,
      },
    });
  }, [
    rgi,
    brand,
    gameName,
    gameId,
    provider,
    providerRef,
    gameUid,
    runtime,
    amplitudeExperiments,
  ]);
};
